import { ChangeDetectorRef } from '@angular/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { PlanLimitError } from '../../features/plans/models/enforcement.model';
import { SessionInfoDto } from '../../models/chat/chat-session.models';
import {
  ChatInitialSessionLoadResult,
  ChatSelectedSessionLoadResult,
} from '../../models/chat-ui.model';
import { ChatService } from '../../services/chat/api/chat.service';
import { FrontendLoggerService } from '../../services/frontend-logger.service';
import { LanguageService } from '../../services/language/language.service';
import { NotificationService } from '../../services/notification.service';
import { ChatDomEffectsService } from '../../services/chat/presentation/chat-dom-effects.service';
import { ChatFeedbackUiService } from '../../services/chat/presentation/chat-feedback-ui.service';
import { ChatIdService } from '../../services/chat/presentation/chat-id.service';
import { ChatSessionLoaderService } from '../../services/chat/session/chat-session-loader.service';
import { ChatSessionUiService } from '../../services/chat/session/chat-session-ui.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { formatChatError } from './chat-runtime.models';
import { ChatSessionStatePort } from './chat-runtime.ports';
import { ChatRequestTracker } from './chat-request.tracker';
import { ChatRoleComposerController } from './chat-role-composer.controller';

export class ChatSessionController {
  constructor(
    private readonly state: ChatSessionStatePort,
    private readonly chatService: ChatService,
    private readonly loader: ChatSessionLoaderService,
    private readonly storage: ChatStorageService,
    private readonly sessionUi: ChatSessionUiService,
    private readonly dom: ChatDomEffectsService,
    private readonly feedback: ChatFeedbackUiService,
    private readonly ids: ChatIdService,
    private readonly notifications: NotificationService,
    private readonly language: LanguageService,
    private readonly logger: FrontendLoggerService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly roles: ChatRoleComposerController,
    private readonly requests: ChatRequestTracker
  ) {}

  loadInitial(generateId: () => string, onResult: (result: ChatInitialSessionLoadResult) => void): void {
    this.requests.start();
    this.loader.loadInitialSession(generateId).pipe(
      finalize(() => this.requests.end()),
      takeUntil(this.state.destroy$)
    ).subscribe({
      next: (result) => {
        onResult(result);
        this.requests.endIfStuck();
      },
      complete: () => {
        this.logger.info('Sesiones cargadas correctamente', 'ChatComponent.ngOnInit');
        this.requests.endIfStuck();
      },
    });
  }

  applyInitial(result: ChatInitialSessionLoadResult): void {
    if (result.kind === 'session-list') {
      this.state.sessionList = result.showSessionModal ? result.sessions : [];
      this.state.showSessionModal = result.showSessionModal;
      return;
    }
    if (result.kind === 'session-loaded') {
      this.applySelected(result);
      this.state.selectedSessionId = result.sessionId;
      this.storage.setSessionId(result.sessionId);
      this.scrollToBottom();
      return;
    }
    this.logger.error(
      'Error al obtener sesiones: ' + formatChatError(result.error),
      'ChatComponent.ngOnInit'
    );
    this.state.showSessionModal = result.showSessionModal;
    this.state.sessionList = result.sessions;
  }

  applySelected(result: ChatSelectedSessionLoadResult): void {
    this.state.messages = result.messages;
    this.logger.info(
      `[ChatComponent] Total messages after mapping: ${JSON.stringify(this.state.messages.length)}`
    );
    // `showDetails` cuelga de que HAYA fuentes, no de que alguna esté seleccionada: si
    // colgara de `hasSelection`, el panel dejaría de abrirse justo cuando hay varias
    // fuentes y ninguna seleccionada, que es cuando más hay para ver.
    this.state.currentSources = result.selectedState.sources;
    this.state.selectedChunkId = result.selectedState.selectedChunkId;
    this.state.selectedSource = result.selectedState.selectedSource;
    this.state.showDetails = result.selectedState.sources.length > 0;
  }

  selectSession(sessionId: string, generateId: () => string): void {
    this.state.showSessionModal = false;
    this.roles.clear();
    this.requests.start();
    this.loader.loadSessionById(sessionId, generateId).pipe(
      finalize(() => this.requests.end()),
      takeUntil(this.state.destroy$)
    ).subscribe({
      next: (result) => {
        this.applySelected(result);
        this.scrollToBottom();
        this.requests.endIfStuck();
      },
      error: (error) => {
        this.logger.error(
          'Error al obtener sesión: ' + formatChatError(error),
          'ChatComponent.onSessionSelected'
        );
        this.requests.endIfStuck();
      },
    });
  }

  recoverSession(generateId: () => string): void {
    if (!this.state.selectedSessionId) return;
    this.selectSession(this.state.selectedSessionId, generateId);
    this.storage.setSessionId(this.state.selectedSessionId);
  }

  newSession(): void {
    // Decisión fundador (2026-07-21): sesión nueva = localStorage vacío (salvo auth y
    // preferencias críticas) para que ningún estado colgado (rol experto, doc anclado,
    // sesión previa) sobreviva y la UI coincida con lo que realmente viaja al backend.
    this.wipeDanglingChatState();
    const sessionId = this.ids.createSessionId();
    this.storage.setSessionId(sessionId);
    this.state.showSessionModal = false;
    this.resetConversation();
    this.state.planLimitError = null;
    this.logger.info(`Nueva sesión iniciada: ${sessionId}`, 'ChatComponent.onNewSession');
  }

  clearHistory(): void {
    this.logger.info('Limpiando historial de chat', 'ChatComponent.clearChatHistory');
    this.resetConversation();
    // Misma decisión que en `newSession()`: limpiar es dejar TODO vacío, no solo la sesión.
    this.wipeDanglingChatState();
    this.sessionUi.setSelectedSessionId(null);
    this.dom.reload();
  }

  closeModal(): void {
    this.storage.setSessionId('0');
    this.state.showSessionModal = false;
  }

  clearLimitedDocument(): void {
    try {
      this.storage.clearLimitedDocument();
    } catch {
      this.logger.warn(
        'No se pudo limpiar limitChatDocId de localStorage',
        'ChatComponent.onClearLimitedDoc'
      );
    }
    this.state.limitedDocValue = null;
    this.state.limitedDocId = null;
    this.state.limitedDocName = null;
    this.changeDetector.markForCheck();
  }

  showPlanLimit(error: PlanLimitError): void {
    this.state.planLimitError = error;
    this.notifications.addNotification({
      type: 'warning',
      message: this.language.translate('enforcement.banner.body'),
      priority: 'high',
    });
    this.scrollToBottom();
    this.changeDetector.markForCheck();
  }

  changeName(session: SessionInfoDto): void {
    if (!session.session_id) return;
    this.state.editingSessionId = session.session_id;
    this.state.editingSessionName = session.conversation_name || '';
    this.dom.focusByIdDeferred(`session-edit-input-${session.session_id}`);
  }

  saveName(session: SessionInfoDto): void {
    const sessionId = session.session_id;
    if (!sessionId) {
      this.cancelNameEdit();
      return;
    }
    const name = this.state.editingSessionName.trim();
    if (!name) {
      this.state.sessionNameError = true;
      return;
    }
    this.state.sessionNameError = false;
    if (name !== session.conversation_name) this.persistName(session, name);
    this.state.editingSessionId = null;
    this.state.editingSessionName = '';
  }

  cancelNameEdit(): void {
    this.state.editingSessionId = null;
    this.state.editingSessionName = '';
    this.state.sessionNameError = false;
  }

  deriveTitle(text: string): string {
    const clean = text.replace(/\s+/g, ' ').trim();
    if (clean.length <= 40) return clean;
    const slice = clean.slice(0, 40);
    const lastSpace = slice.lastIndexOf(' ');
    return `${(lastSpace >= 24 ? slice.slice(0, lastSpace) : slice).trim()}…`;
  }

  maybeAutoTitle(sessionId: string | null): void {
    const source = this.state.pendingAutoTitleText;
    this.state.pendingAutoTitleText = null;
    if (!source || !sessionId || sessionId === '0') return;
    const title = this.deriveTitle(source);
    if (!title) return;
    this.chatService.updateConversationName(sessionId, title)
      .pipe(takeUntil(this.state.destroy$))
      .subscribe({
        next: () => {
          const existing = this.state.sessionList.find((item) => item.session_id === sessionId);
          if (existing && !existing.conversation_name) existing.conversation_name = title;
          this.logger.info(
            `Auto-título aplicado a la sesión ${sessionId}`,
            'ChatComponent.maybeAutoTitleSession'
          );
          this.changeDetector.markForCheck();
        },
        error: (error) => this.logger.warn(
          'No se pudo aplicar el auto-título: ' + formatChatError(error),
          'ChatComponent.maybeAutoTitleSession'
        ),
      });
  }

  scrollToBottom(): void {
    try { this.dom.scrollToBottom(this.state.chatContainer?.nativeElement); } catch { }
  }

  /**
   * Borra el localStorage completo (preservando auth y preferencias críticas) y resetea
   * los selectores en memoria (rol experto, documento anclado, chunk seleccionado) para
   * que el estado visual no prometa nada que el payload no vaya a enviar.
   */
  private wipeDanglingChatState(): void {
    this.storage.clearAllExceptCritical();
    this.roles.clear();
    this.state.limitedDocValue = null;
    this.state.limitedDocId = null;
    this.state.limitedDocName = null;
    this.state.selectedChunkId = null;
    this.changeDetector.markForCheck();
  }

  private resetConversation(): void {
    this.state.messages = [];
    this.state.currentSources = [];
    this.state.selectedSource = null;
    this.state.showFeedback = false;
    this.applyClosedFeedback();
    this.state.error = null;
  }

  private applyClosedFeedback(): void {
    const state = this.feedback.buildClosedState();
    this.state.feedbackTargetIndex = state.feedbackTargetIndex;
    this.state.feedbackLike = state.feedbackLike;
    this.state.feedbackSent = state.feedbackSent;
    this.state.feedbackRating = state.feedbackRating;
    this.state.hoverRating = state.hoverRating;
    this.state.feedbackComment = state.feedbackComment;
  }

  private persistName(session: SessionInfoDto, name: string): void {
    const sessionId = session.session_id!;
    session.conversation_name = name;
    this.chatService.updateConversationName(sessionId, name)
      .pipe(takeUntil(this.state.destroy$))
      .subscribe({
        next: () => {
          this.state.showSessionNameSuccess = sessionId;
          setTimeout(() => { this.state.showSessionNameSuccess = null; }, 2000);
        },
        error: (error) => this.logger.error(
          'Error al actualizar el nombre de la sesión: ' + formatChatError(error),
          'ChatComponent.saveSessionName'
        ),
      });
  }
}
